import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { environment } from 'src/environments/environment';
import cloneDeep from 'lodash/cloneDeep';
import { ResourceType } from 'src/app/models/ResourceType';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '../services/data.service';
import { ParamDetail } from '../models/ParamDetail';
import { ParserService } from '../services/parser.service';

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireService {
  private readonly dataService = inject(DataService);
  private readonly translate = inject(TranslateService);
  private readonly parser = inject(ParserService);

  readonly performerTypeExt =
    'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-performerType';

  async callPackage(
    packageEndpoint: string,
    paramDetails: ParamDetail[],
  ): Promise<fhirR4.Bundle> {
    const params = this.createPackageParameters(paramDetails);
    return await this.dataService
      .post<fhirR4.Parameters>(params, packageEndpoint)
      .then(
        (parameters) =>
          parameters.parameter?.find((param) => param.name === 'PackageBundle')
            ?.resource as fhirR4.Bundle,
      ) // currently taking the just first PackageBundle and ignoring the rest, this needs to be fixed later
      .then((qPackage) => this.sanitize(qPackage))
      .catch((e) => {
        console.log(e);
        throw new Error(this.translate.instant('Error.RetrieveQuestionnaire'));
      });
  }

  sanitize(qPackage: fhirR4.Bundle): fhirR4.Bundle {
    const questionnaire = this.parser.getQuestionnaireFromPackage(qPackage);
    if (questionnaire?.version)
      questionnaire.version = questionnaire.version.replaceAll('+', '-');
    const questionnaireEntry =
      this.parser.getQuestionnaireEntryFromPackage(qPackage);
    if (questionnaireEntry?.request?.ifNoneExist)
      questionnaireEntry.request.ifNoneExist =
        questionnaireEntry.request.ifNoneExist.replaceAll('+', '-');
    return qPackage;
  }

  createPackageParameters(paramDetails: ParamDetail[]) {
    const params = new fhirR4.Parameters();
    params.resourceType = 'Parameters';
    params.parameter = paramDetails.map((paramDetail) => {
      const parametersParameter = new fhirR4.ParametersParameter();
      parametersParameter.name = paramDetail.name;
      parametersParameter[paramDetail.type] = paramDetail.value;
      return parametersParameter;
    });
    return params;
  }

  async postPackage(qpkg: fhirR4.Bundle): Promise<fhirR4.Bundle> {
    return await this.dataService
      .post<fhirR4.Bundle>(qpkg, environment.dtrBackend ?? '')
      .catch((e) => {
        console.log(e);
        throw new Error(this.translate.instant('Error.Configuration'));
      });
  }

  processValueSet(qPackage?: fhirR4.Bundle): fhirR4.Questionnaire {
    let questionnaire;
    if (!qPackage?.entry || qPackage.entry.length < 1) {
      throw new Error(this.translate.instant('Error.RetrieveQuestionnaire'));
    }

    const valueSetMap = this.generateValueSetMap(qPackage);

    qPackage.entry
      ?.filter(
        (element) =>
          element.resource?.resourceType === ResourceType.Questionnaire,
      )
      .map((element) => {
        questionnaire = cloneDeep(element.resource) as fhirR4.Questionnaire;
        this.expandItems(questionnaire.item, valueSetMap);
      });
    return questionnaire;
  }

  expandItems(item, valueSetMap): void {
    if (!item || item.length < 1) return;
    item.forEach((member) => {
      if (member.answerValueSet) {
        const key = member.answerValueSet;
        delete member.answerValueSet;
        member.answerOption = valueSetMap[key];
      }
      if (member.item?.length > 0) {
        return this.expandItems(member.item, valueSetMap);
      }
    });
  }

  generateValueSetMap(qPackage: fhirR4.Bundle) {
    const valueSetMap = {};
    qPackage.entry
      ?.filter(
        (element) => element.resource?.resourceType === ResourceType.ValueSet,
      )
      .map((element) => {
        const valueSet = element.resource as fhirR4.ValueSet;
        const key = valueSet.url ?? 'nourl';
        if (this.expansionExists(valueSet)) {
          valueSetMap[key] = valueSet.expansion?.contains?.map((code) => {
            return { valueCoding: code };
          });
        } else if (this.composeExists(valueSet)) {
          valueSet.compose?.include.map(
            (code) =>
              (valueSetMap[key] = code.concept?.map((item) => {
                return {
                  valueCoding: {
                    system: code.system,
                    code: item.code,
                    display: item.display,
                  },
                };
              })),
          );
        }
      });
    return valueSetMap;
  }

  private composeExists(valueSet: fhirR4.ValueSet) {
    return valueSet.compose && valueSet.compose?.include?.length > 0;
  }

  private expansionExists(valueSet: fhirR4.ValueSet) {
    return (
      valueSet.expansion?.contains && valueSet.expansion?.contains?.length > 0
    );
  }

  isValidPerformerType(questionnaire: fhirR4.Questionnaire) {
    const performerType = questionnaire.extension?.find(
      (ext) => ext.url === this.performerTypeExt,
    )?.valueCode;
    const authorType = this.dataService.fhirUser.split('/')[0];
    const allowedMatches = [
      ResourceType.Practitioner,
      ResourceType.PractitionerRole,
    ] as string[];
    return (
      !performerType ||
      performerType === authorType ||
      (allowedMatches.includes(performerType) &&
        allowedMatches.includes(authorType))
    );
  }
}
